import { useState } from 'react';
import type { TypeDeVin } from '../domain/cellar';
import type { FilterOptions, InventoryFilters, RatingFilterValue } from '../domain/filters';
import { countryLabel, regionFilterLabel, sortedByLabel, t, tf } from '../i18n';
import { wineTypeLabel } from '../i18n/errors';
import { MultiSelectField } from './MultiSelectField';
import './FilterBar.css';

const WINE_TYPES: TypeDeVin[] = ['rouge', 'blanc', 'rose', 'vendanges_tardives'];
const RATING_CHOICES: RatingFilterValue[] = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 'unrated'];

/** How many filters are set besides the search text (shown on the "Filtres" button). */
function panelFilterCount(filters: InventoryFilters): number {
  return (
    filters.typesDeVin.length +
    filters.domaines.length +
    filters.drinkUntilYears.length +
    filters.annees.length +
    filters.pays.length +
    filters.regions.length +
    filters.cepages.length +
    filters.ratings.length +
    (filters.includeEmpty ? 1 : 0) +
    (filters.due !== undefined ? 1 : 0)
  );
}

/**
 * The inventory's search and filters (T082, FR-019 to FR-022, contracts/ui-and-emails.md
 * "Filters"): the free-text search always visible, and behind "Filtres" drop-downs where several
 * values can be ticked: Type, Pays, Région (the ticked countries' regions, owner 2026-10-03),
 * Domaine, Année, Cépage (each variety of a blend on its own), Note ("Pas de note" for none) and
 * the "à boire avant" years, then "Inclure les vins épuisés" last (owner, 2026-10-15: Type,
 * Domaine and the years became multiple, Région and Domaine searchable). Kept compact for a phone.
 */
export function FilterBar({
  filters,
  options,
  regionsFor,
  onChange,
  showIncludeEmpty = true,
}: {
  filters: InventoryFilters;
  options: FilterOptions;
  /** The Région choices for the ticked countries (domain/filters.ts `regionChoices`). */
  regionsFor: (pays: readonly string[]) => string[];
  onChange: (filters: InventoryFilters) => void;
  /** "Inclure les vins épuisés"; off on "À boire", which lists wines in stock only. */
  showIncludeEmpty?: boolean;
}) {
  const active = panelFilterCount(filters);
  const [open, setOpen] = useState(active > 0);

  return (
    <section className="filter-bar">
      <div className="filter-bar__search">
        <label htmlFor="inventory-search">{t('filters.search')}</label>
        <input
          id="inventory-search"
          type="search"
          placeholder={t('filters.searchPlaceholder')}
          value={filters.text}
          onChange={(event) => onChange({ ...filters, text: event.target.value })}
        />
      </div>

      <button
        type="button"
        className="filter-bar__toggle"
        aria-expanded={open}
        aria-controls="inventory-filters"
        onClick={() => setOpen(!open)}
      >
        {open
          ? t('filters.hide')
          : active > 0
            ? tf('filters.showActive', { count: String(active) })
            : t('filters.show')}
      </button>

      {open && (
        <div id="inventory-filters" className="filter-bar__panel">
          <MultiSelectField
            label={t('wine.typeDeVin')}
            choices={WINE_TYPES.map((type) => ({ value: type, label: wineTypeLabel(type) }))}
            selected={filters.typesDeVin}
            onChange={(types) => onChange({ ...filters, typesDeVin: types as TypeDeVin[] })}
          />
          <MultiSelectField
            label={t('wine.pays')}
            choices={sortedByLabel(
              options.pays.map((code) => ({ value: code, label: countryLabel(code) })),
              (country) => country.label,
            )}
            selected={filters.pays}
            onChange={(pays) => {
              // Ticked regions of countries no longer ticked are dropped.
              const offered = regionsFor(pays);
              const regions = filters.regions.filter((region) => offered.includes(region));
              onChange({ ...filters, pays, regions });
            }}
          />
          <MultiSelectField
            label={t('wine.region')}
            choices={regionsFor(filters.pays).map((region) => ({
              value: region,
              label: regionFilterLabel(region),
            }))}
            selected={filters.regions}
            onChange={(regions) => onChange({ ...filters, regions })}
            searchable
          />
          <MultiSelectField
            label={t('wine.domaine')}
            choices={options.domaines}
            selected={filters.domaines}
            onChange={(domaines) => onChange({ ...filters, domaines })}
            searchable
          />
          <MultiSelectField
            label={t('wine.annee')}
            choices={options.annees.map(String)}
            selected={filters.annees.map(String)}
            onChange={(annees) => onChange({ ...filters, annees: annees.map(Number) })}
          />

          <MultiSelectField
            label={t('wine.cepage')}
            choices={options.cepages}
            selected={filters.cepages}
            onChange={(cepages) => onChange({ ...filters, cepages })}
            searchable
          />

          <MultiSelectField
            label={t('filters.rating')}
            choices={RATING_CHOICES.map((value) => ({
              value: String(value),
              label: value === 'unrated' ? t('rating.none') : String(value),
            }))}
            selected={filters.ratings.map(String)}
            onChange={(ratings) =>
              onChange({
                ...filters,
                ratings: ratings.map((value): RatingFilterValue =>
                  value === 'unrated' ? 'unrated' : Number(value),
                ),
              })
            }
          />

          <MultiSelectField
            label={t('filters.due')}
            choices={options.drinkUntilYears.map(String)}
            selected={filters.drinkUntilYears.map(String)}
            onChange={(years) => onChange({ ...filters, drinkUntilYears: years.map(Number) })}
          />

          {showIncludeEmpty && (
            <label className="filter-bar__empty">
              <input
                type="checkbox"
                checked={filters.includeEmpty}
                onChange={(event) => onChange({ ...filters, includeEmpty: event.target.checked })}
              />
              {t('filters.includeEmpty')}
            </label>
          )}
        </div>
      )}
    </section>
  );
}
